/**
 * PTW Validation Utilities
 *
 * Common validation functions for Permit to Work (PTW) module,
 * including gas test reading validation with safety limits.
 */

// Gas reading safe ranges based on industry standards
export const GAS_SAFE_RANGES = {
  oxygen_level: { min: 19.5, max: 23.5, unit: '%', name: 'Oxygen' },
  lel_level: { min: 0, max: 10, unit: '%', name: 'LEL' },
  h2s_level: { min: 0, max: 10, unit: 'ppm', name: 'H₂S' },
  co_level: { min: 0, max: 35, unit: 'ppm', name: 'CO' }
};

/**
 * Get warning messages for a gas test reading
 * @param {Object} reading - Gas test reading object with oxygen_level, lel_level, h2s_level, co_level
 * @returns {Array<string>} Array of warning messages for unsafe readings
 */
export const getGasReadingWarnings = (reading) => {
  const warnings = [];

  if (reading.oxygen_level !== '' && reading.oxygen_level !== null && reading.oxygen_level !== undefined) {
    const value = parseFloat(reading.oxygen_level);
    if (!isNaN(value)) {
      if (value < GAS_SAFE_RANGES.oxygen_level.min) {
        warnings.push(`Oxygen level (${value}%) is below safe range (${GAS_SAFE_RANGES.oxygen_level.min}% - ${GAS_SAFE_RANGES.oxygen_level.max}%) - OXYGEN DEFICIENT`);
      } else if (value > GAS_SAFE_RANGES.oxygen_level.max) {
        warnings.push(`Oxygen level (${value}%) is above safe range (${GAS_SAFE_RANGES.oxygen_level.min}% - ${GAS_SAFE_RANGES.oxygen_level.max}%) - OXYGEN ENRICHED`);
      }
    }
  }

  if (reading.lel_level !== '' && reading.lel_level !== null && reading.lel_level !== undefined) {
    const value = parseFloat(reading.lel_level);
    if (!isNaN(value) && value > GAS_SAFE_RANGES.lel_level.max) {
      warnings.push(`LEL (${value}%) exceeds safe limit (<${GAS_SAFE_RANGES.lel_level.max}%) - EXPLOSIVE ATMOSPHERE RISK`);
    }
  }

  if (reading.h2s_level !== '' && reading.h2s_level !== null && reading.h2s_level !== undefined) {
    const value = parseFloat(reading.h2s_level);
    if (!isNaN(value) && value > GAS_SAFE_RANGES.h2s_level.max) {
      warnings.push(`H₂S (${value} ppm) exceeds safe limit (<${GAS_SAFE_RANGES.h2s_level.max} ppm) - TOXIC GAS HAZARD`);
    }
  }

  if (reading.co_level !== '' && reading.co_level !== null && reading.co_level !== undefined) {
    const value = parseFloat(reading.co_level);
    if (!isNaN(value) && value > GAS_SAFE_RANGES.co_level.max) {
      warnings.push(`CO (${value} ppm) exceeds safe limit (<${GAS_SAFE_RANGES.co_level.max} ppm) - TOXIC GAS HAZARD`);
    }
  }

  return warnings;
};

/**
 * Check if a specific gas reading field has an unsafe value
 * @param {Object} reading - Gas test reading object
 * @param {string} field - Field name (oxygen_level, lel_level, h2s_level, co_level)
 * @returns {boolean} True if the field value is outside safe range
 */
export const isGasReadingFieldUnsafe = (reading, field) => {
  const value = reading[field];
  if (value === '' || value === null || value === undefined) return false;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return false;

  const range = GAS_SAFE_RANGES[field];
  if (!range) return false;

  return numValue < range.min || numValue > range.max;
};

/**
 * Check if any gas reading in an array has unsafe values
 * @param {Array<Object>} readings - Array of gas test readings
 * @returns {boolean} True if any reading has unsafe values
 */
export const hasAnyUnsafeGasReadings = (readings) => {
  if (!readings || readings.length === 0) return false;
  return readings.some(reading => getGasReadingWarnings(reading).length > 0);
};

/**
 * Order a PTW safety checklist for display so answers are grouped by the
 * selected option — YES, then NO, then NA, then unanswered — WITHIN each
 * permit-type group. Grouping is preserved (items stay contiguous by type/
 * custom) so per-type section headers still render correctly for every type,
 * not just the first. Mirrors the generated PDF report ordering.
 * @param {Array<Object>} list - safety_checklist items
 * @returns {Array<Object>} a new, reordered array
 */
export const orderChecklistByStatus = (list) => {
  if (!Array.isArray(list) || list.length === 0) return list || [];
  const rank = { yes: 0, no: 1, na: 2 };
  const statusOf = (i) => i.status || (i.checked ? 'yes' : null);
  const groupKeys = [];
  const byKey = {};
  list.forEach((it) => {
    const key = it.is_custom ? '__custom__' : (it.permit_type || '__general__');
    if (!byKey[key]) { byKey[key] = []; groupKeys.push(key); }
    byKey[key].push(it);
  });
  return groupKeys.flatMap((k) =>
    [...byKey[k]].sort((a, b) => (rank[statusOf(a)] ?? 3) - (rank[statusOf(b)] ?? 3))
  );
};
